import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Truck, MapPin, Gauge, User as UserIcon, AlertTriangle, Users, Plus, Trash2, Fuel, Camera, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VehicleForm - Sub-formulario de bitácora del vehículo.
 * Props:
 *  - value: objeto con los datos actuales (o null)
 *  - onChange: (data) => void  actualiza el estado en el padre
 *  - tiendaId: string para autocompletar placas/conductores
 *  - paymentId: (opcional) si existe, permite subir fotos de daños directamente aquí
 *  - files: (opcional) archivos ya subidos al pago, para marcar como fotos de daño
 *  - onFilesRefresh: (opcional) callback tras subir foto, para refrescar `files` en el padre
 *  - onOpenPhotos: (opcional) callback para abrir el flujo de subir fotos si estamos en detalle
 */
export default function VehicleForm({ value, onChange, tiendaId, paymentId, files = [], onFilesRefresh, onOpenPhotos }) {
  const data = value || {};
  const [placasHistory, setPlacasHistory] = useState([]);
  const [conductorHistory, setConductorHistory] = useState([]);
  const [uploadingDamage, setUploadingDamage] = useState(false);
  const damageFileRef = useRef(null);
  const damageCameraRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c] = await Promise.all([
          axios.get(`${API}/vehiculo/history?field=placas${tiendaId ? `&tienda_id=${tiendaId}` : ''}`),
          axios.get(`${API}/vehiculo/history?field=conductor${tiendaId ? `&tienda_id=${tiendaId}` : ''}`),
        ]);
        setPlacasHistory(p.data?.values || []);
        setConductorHistory(c.data?.values || []);
      } catch { /* silencioso */ }
    };
    load();
  }, [tiendaId]);

  const kmRecorridos = useMemo(() => {
    const ini = parseFloat(data.km_inicial);
    const fin = parseFloat(data.km_final);
    if (isNaN(ini) || isNaN(fin)) return null;
    return fin - ini;
  }, [data.km_inicial, data.km_final]);

  const kmWarning = kmRecorridos !== null && kmRecorridos < 0
    ? 'El km final es menor al inicial. Corrige antes de guardar.'
    : kmRecorridos !== null && kmRecorridos > 500
      ? `Recorrido de ${kmRecorridos.toFixed(0)} km — verifica que sea correcto.`
      : null;

  const set = (patch) => onChange({ ...data, ...patch });

  const addPasajero = () => {
    const arr = Array.isArray(data.pasajeros) ? [...data.pasajeros] : [];
    arr.push({ nombre: '', motivo: '' });
    set({ pasajeros: arr, pasajeros_reportados: true });
  };
  const updatePasajero = (idx, key, val) => {
    const arr = [...(data.pasajeros || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    set({ pasajeros: arr });
  };
  const removePasajero = (idx) => {
    const arr = [...(data.pasajeros || [])];
    arr.splice(idx, 1);
    set({ pasajeros: arr, pasajeros_reportados: arr.length > 0 });
  };

  const toggleDamageFoto = (fileId) => {
    const arr = new Set(data.danos_fotos_ids || []);
    if (arr.has(fileId)) arr.delete(fileId); else arr.add(fileId);
    set({ danos_fotos_ids: Array.from(arr) });
  };

  const uploadDamagePhoto = async (file) => {
    if (!paymentId) {
      toast.error('Guarda primero el pago para poder subir fotos');
      return;
    }
    setUploadingDamage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('payment_id', paymentId);
      const res = await axios.post(`${API}/files/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newFileId = res.data?.id;
      if (newFileId) {
        const arr = Array.isArray(data.danos_fotos_ids) ? [...data.danos_fotos_ids] : [];
        if (!arr.includes(newFileId)) arr.push(newFileId);
        set({ danos_fotos_ids: arr });
      }
      toast.success('Foto subida');
      if (onFilesRefresh) onFilesRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la foto');
    } finally {
      setUploadingDamage(false);
    }
  };

  const handleDamageFileSelect = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = ''; // reset input
    list.forEach(uploadDamagePhoto);
  };

  return (
    <Card className="p-5 border-amber-200 dark:border-amber-900/60 bg-gradient-to-br from-amber-50/60 via-white to-white dark:from-amber-950/30 dark:via-slate-900 dark:to-slate-900" data-testid="vehicle-form-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-950/60 dark:to-amber-900/40 flex items-center justify-center shrink-0">
          <Truck className="h-5 w-5 text-amber-700 dark:text-amber-300" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-display font-semibold text-slate-900 dark:text-slate-100">Bitácora del vehículo <span className="text-red-500">*</span></h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Obligatoria para este proveedor. Puedes guardar el km inicial ahora y completar km final al regresar.</p>
        </div>
      </div>

      {/* Origen y destino */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><MapPin className="h-3 w-3" /> Origen <span className="text-red-500">*</span></Label>
          <Input value={data.destino_origen || ''} onChange={(e) => set({ destino_origen: e.target.value })} placeholder="Ej: Tienda Centro" required data-testid="vehicle-origen" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><MapPin className="h-3 w-3" /> Destino <span className="text-red-500">*</span></Label>
          <Input value={data.destino_destino || ''} onChange={(e) => set({ destino_destino: e.target.value })} placeholder="Ej: Escuintla" required data-testid="vehicle-destino" />
        </div>
      </div>

      {/* Placas y conductor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">Placas del vehículo <span className="text-red-500">*</span></Label>
          <Input
            value={data.placas || ''}
            onChange={(e) => set({ placas: e.target.value.toUpperCase() })}
            placeholder="P-123ABC"
            list="placas-history"
            required
            data-testid="vehicle-placas"
          />
          <datalist id="placas-history">
            {placasHistory.map((p, i) => <option key={`${p}-${i}`} value={p} />)}
          </datalist>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><UserIcon className="h-3 w-3" /> Conductor <span className="text-red-500">*</span></Label>
          <Input
            value={data.conductor || ''}
            onChange={(e) => set({ conductor: e.target.value })}
            placeholder="Nombre del conductor"
            list="conductor-history"
            required
            data-testid="vehicle-conductor"
          />
          <datalist id="conductor-history">
            {conductorHistory.map((c, i) => <option key={`${c}-${i}`} value={c} />)}
          </datalist>
        </div>
      </div>

      {/* Kilometraje */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><Gauge className="h-3 w-3" /> Km inicial <span className="text-red-500">*</span></Label>
          <Input type="number" step="1" min="0" value={data.km_inicial ?? ''} onChange={(e) => set({ km_inicial: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="Ej: 45210" required data-testid="vehicle-km-inicial" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><Gauge className="h-3 w-3" /> Km final <span className="text-[10px] text-slate-400 dark:text-slate-500">(al regresar)</span></Label>
          <Input type="number" step="1" min="0" value={data.km_final ?? ''} onChange={(e) => set({ km_final: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="Opcional" data-testid="vehicle-km-final" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1"><Fuel className="h-3 w-3" /> Tanque restante (L o km)</Label>
          <Input type="number" step="0.1" min="0" value={data.tanque_restante_litros ?? ''} onChange={(e) => set({ tanque_restante_litros: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="Ej: 12" data-testid="vehicle-tanque" />
        </div>
      </div>

      {kmRecorridos !== null && (
        <div className={`mb-4 flex items-center justify-between rounded-lg px-3 py-2 text-xs ${kmWarning ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/60' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800'}`}>
          <span className="flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5" />
            <span>Recorrido: <strong>{kmRecorridos.toFixed(0)} km</strong></span>
          </span>
          {kmWarning && <span className="font-medium">{kmWarning}</span>}
        </div>
      )}

      {/* Daños */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            ¿Hay daños que reportar?
          </Label>
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
            <button
              type="button"
              onClick={() => set({ danos_reportados: false, danos_descripcion: '', danos_fotos_ids: [] })}
              className={`px-3 py-1 text-xs font-medium ${!data.danos_reportados ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-white dark:bg-slate-900 text-slate-500'}`}
              data-testid="damage-no"
            >
              No
            </button>
            <button
              type="button"
              onClick={() => set({ danos_reportados: true })}
              className={`px-3 py-1 text-xs font-medium border-l border-slate-200 dark:border-slate-800 ${data.danos_reportados ? 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300' : 'bg-white dark:bg-slate-900 text-slate-500'}`}
              data-testid="damage-yes"
            >
              Sí
            </button>
          </div>
        </div>
        {data.danos_reportados && (
          <div className="space-y-3" data-testid="damage-section">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">Descripción de los hechos</Label>
              <Textarea
                rows={3}
                value={data.danos_descripcion || ''}
                onChange={(e) => set({ danos_descripcion: e.target.value })}
                placeholder="Breve descripción de lo ocurrido, ubicación del daño, condiciones del viaje..."
                data-testid="damage-description"
              />
            </div>
            {/* Uploader de fotos de daños */}
            <div className="rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/60 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-xs font-semibold text-red-800 dark:text-red-300">Fotos del daño ({(data.danos_fotos_ids || []).length})</span>
                </div>
                {paymentId ? (
                  <div className="flex gap-2">
                    <input
                      ref={damageFileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleDamageFileSelect}
                      className="hidden"
                      data-testid="damage-file-input"
                    />
                    <input
                      ref={damageCameraRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleDamageFileSelect}
                      className="hidden"
                      data-testid="damage-camera-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => damageCameraRef.current?.click()}
                      disabled={uploadingDamage}
                      className="text-xs h-8 border-red-300 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40"
                      data-testid="damage-take-photo"
                    >
                      <Camera className="h-3.5 w-3.5 mr-1" /> Cámara
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => damageFileRef.current?.click()}
                      disabled={uploadingDamage}
                      className="text-xs h-8 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="damage-upload-btn"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      {uploadingDamage ? 'Subiendo...' : 'Subir foto'}
                    </Button>
                  </div>
                ) : (
                  <span className="text-[11px] text-red-700 dark:text-red-400 italic">Podrás subir fotos al guardar el pago</span>
                )}
              </div>

              {/* Thumbnails de fotos marcadas como daño */}
              {(data.danos_fotos_ids || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="damage-photos-grid">
                  {(data.danos_fotos_ids || []).map(fid => {
                    const f = files.find(x => x.id === fid);
                    return (
                      <DamagePhotoThumb
                        key={fid}
                        fileId={fid}
                        filename={f?.original_filename}
                        onRemove={() => toggleDamageFoto(fid)}
                      />
                    );
                  })}
                </div>
              )}

              {/* Marcar fotos ya subidas al pago que no están en el set */}
              {paymentId && files.filter(f => /\.(png|jpe?g|gif|webp)$/i.test(f.original_filename) && !(data.danos_fotos_ids || []).includes(f.id)).length > 0 && (
                <details className="mt-3 group">
                  <summary className="text-[11px] text-red-700 dark:text-red-400 cursor-pointer hover:underline select-none">
                    o marcar fotos ya subidas al pago...
                  </summary>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {files.filter(f => /\.(png|jpe?g|gif|webp)$/i.test(f.original_filename) && !(data.danos_fotos_ids || []).includes(f.id)).map(f => (
                      <button
                        type="button"
                        key={f.id}
                        onClick={() => toggleDamageFoto(f.id)}
                        className="text-left rounded-md border border-slate-200 dark:border-slate-700 hover:border-red-300 dark:hover:border-red-800 p-2 text-xs bg-white dark:bg-slate-900 transition-colors"
                        data-testid={`damage-photo-toggle-${f.id}`}
                      >
                        <span className="inline-block h-3 w-3 rounded-sm mr-1 border border-slate-300 dark:border-slate-600 align-middle" />
                        <span className="text-slate-700 dark:text-slate-300 truncate inline-block max-w-[85%] align-middle">{f.original_filename}</span>
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pasajeros */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-600 dark:text-slate-300" />
            ¿Llevó pasajeros?
          </Label>
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
            <button
              type="button"
              onClick={() => set({ pasajeros_reportados: false, pasajeros: [] })}
              className={`px-3 py-1 text-xs font-medium ${!data.pasajeros_reportados ? 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' : 'bg-white dark:bg-slate-900 text-slate-500'}`}
              data-testid="passenger-no"
            >
              No
            </button>
            <button
              type="button"
              onClick={() => { set({ pasajeros_reportados: true }); if ((data.pasajeros || []).length === 0) addPasajero(); }}
              className={`px-3 py-1 text-xs font-medium border-l border-slate-200 dark:border-slate-800 ${data.pasajeros_reportados ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-white dark:bg-slate-900 text-slate-500'}`}
              data-testid="passenger-yes"
            >
              Sí
            </button>
          </div>
        </div>
        {data.pasajeros_reportados && (
          <div className="space-y-2" data-testid="passenger-section">
            {(data.pasajeros || []).map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-[11px] text-slate-500 dark:text-slate-400">Nombre</Label>
                  <Input value={p.nombre || ''} onChange={(e) => updatePasajero(idx, 'nombre', e.target.value)} placeholder="Nombre del pasajero" data-testid={`passenger-name-${idx}`} />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500 dark:text-slate-400">Motivo</Label>
                  <Input value={p.motivo || ''} onChange={(e) => updatePasajero(idx, 'motivo', e.target.value)} placeholder="Ej: apoyo, capacitación" data-testid={`passenger-reason-${idx}`} />
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={() => removePasajero(idx)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`passenger-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={addPasajero} className="mt-1" data-testid="passenger-add">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar pasajero
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Miniatura de foto de daño con auth (blob) y botón para quitar */
function DamagePhotoThumb({ fileId, filename, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    async function load() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/files/${fileId}`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        if (!cancelled) setSrc(url);
      } catch { /* ignore */ }
    }
    load();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);
  return (
    <div className="relative group rounded-md overflow-hidden border border-red-300 dark:border-red-900/60 bg-white dark:bg-slate-900" data-testid={`damage-thumb-${fileId}`}>
      <div className="aspect-square bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        {src ? (
          <img src={src} alt={filename || 'Foto de daño'} className="h-full w-full object-cover" />
        ) : (
          <Camera className="h-6 w-6 text-slate-400 dark:text-slate-600" />
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md opacity-90 hover:opacity-100 transition-opacity"
        aria-label="Quitar foto"
        data-testid={`damage-thumb-remove-${fileId}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function validateVehicleForm(data, opts = {}) {
  const atCreation = !!opts.atCreation;
  if (!data) return { ok: false, error: 'Llena la bitácora del vehículo' };
  const required = [
    ['destino_origen', 'el origen'],
    ['destino_destino', 'el destino'],
    ['placas', 'las placas del vehículo'],
    ['conductor', 'el conductor'],
  ];
  for (const [k, label] of required) {
    if (!(data[k] || '').trim()) {
      return { ok: false, error: `Ingresa ${label} en la bitácora del vehículo` };
    }
  }
  if (atCreation && (data.km_inicial === null || data.km_inicial === undefined || data.km_inicial === '')) {
    return { ok: false, error: 'Ingresa el kilometraje inicial del vehículo' };
  }
  const ini = parseFloat(data.km_inicial);
  const fin = parseFloat(data.km_final);
  if (!isNaN(ini) && !isNaN(fin) && fin < ini) {
    return { ok: false, error: 'El km final no puede ser menor al inicial.' };
  }
  if (data.danos_reportados && !(data.danos_descripcion || '').trim()) {
    return { ok: false, error: 'Describe brevemente los daños reportados.' };
  }
  if (data.pasajeros_reportados) {
    const arr = data.pasajeros || [];
    if (arr.length === 0 || arr.some(p => !(p.nombre || '').trim())) {
      return { ok: false, error: 'Ingresa el nombre de cada pasajero.' };
    }
  }
  return { ok: true };
}

/** Guarda la bitácora en el backend. Devuelve true si OK. */
export async function saveVehicleLog(paymentId, data) {
  const val = validateVehicleForm(data, { atCreation: true });
  if (!val.ok) {
    toast.error(val.error);
    return false;
  }
  try {
    await axios.put(`${API}/payments/${paymentId}/vehiculo`, data);
    return true;
  } catch (err) {
    toast.error(err.response?.data?.detail || 'No se pudo guardar la bitácora del vehículo');
    return false;
  }
}
